import { React, useContext, useEffect, useState } from "react";
import { AppContext } from "../../context/AppContext";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Modal, Spinner } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import View from "../../assets/images/Group.svg";
import ApiConfig from "../../api/ApiConfig";
import { simplePostCall, getWithAuthCall } from "../../api/ApiServices";
import { notifySuccess, notifyError } from "../../sharedComponent/notify";
import Loader from "../../sharedComponent/Loader";
import NoDataComp from "../../sharedComponent/NoDataComp";
import { PageHead, StatusBadge, BoolBadge } from "./ffUi";
import {
  useVehicles,
  VehicleSelect,
  vehicleNameById,
  toDateDisplay,
  todayInput,
} from "./ffHelpers";
import "./FleetFinance.scss";

const animations = {
  initial: { opacity: 0, x: 400 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 100 },
};

const money = (n) =>
  n || n === 0 ? Number(n).toLocaleString(undefined, { minimumFractionDigits: 2 }) : "--";

const todayStr = () => todayInput(); // FF-06: local calendar date, not UTC

const emptyForm = {
  vehicle_id: "",
  lessor: "",
  lease_type: "finance",
  start_date: "",
  term_months: "",
  monthly_payment: "",
  discount_rate_annual: "",
};

const Leases = () => {
  const { sidebar } = useContext(AppContext);
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [leases, setLeases] = useState([]);
  const vehicles = useVehicles(); // U-01: shared cached vehicle list

  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);

  // detail modal
  const [showDetail, setShowDetail] = useState(false);
  const [detail, setDetail] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionBusy, setActionBusy] = useState("");

  // run modal
  const [showRun, setShowRun] = useState(false);
  const [asOf, setAsOf] = useState(todayStr());
  const [runLeaseId, setRunLeaseId] = useState(null);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    getLeases();
  }, []);

  const getLeases = () => {
    setLoading(true);
    simplePostCall(
      ApiConfig.FLEET_FINANCE_LEASE_LIST,
      JSON.stringify({ page: 1, page_limit: 50 })
    )
      .then((data) => {
        setLeases((data?.success || data?.result) ? data?.data || [] : []);
      })
      .catch((err) => {
        console.log("lease list err", err);
        setLeases([]);
      })
      .finally(() => setLoading(false));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSaving(true);
    const body = JSON.stringify({
      vehicle_id: Number(form.vehicle_id),
      lessor: form.lessor,
      lease_type: form.lease_type,
      start_date: form.start_date,
      term_months: Number(form.term_months) || 0,
      monthly_payment: Number(form.monthly_payment) || 0,
      discount_rate_annual: Number(form.discount_rate_annual) || 0,
    });
    simplePostCall(ApiConfig.FLEET_FINANCE_LEASE_ADD, body)
      .then((data) => {
        if (data?.success || data?.result) {
          notifySuccess(
            data?.message ||
              `Lease created (PV ${money(data?.data?.present_value)})`
          );
          setShowForm(false);
          setForm(emptyForm);
          getLeases();
        } else {
          notifyError(data?.message || "Unable to create lease");
        }
      })
      .catch((err) => {
        console.log("lease add err", err);
        notifyError("Something went wrong");
      })
      .finally(() => setSaving(false));
  };

  const openDetail = (leaseId) => {
    setShowDetail(true);
    setDetail(null);
    setSchedule([]);
    setDetailLoading(true);
    getWithAuthCall(ApiConfig.FLEET_FINANCE_LEASE_SINGLE + leaseId)
      .then((data) => {
        if (data?.success || data?.result) {
          const d = data?.data || {};
          setDetail(d.lease || d.header || d);
          setSchedule(d.schedule || d.lines || []);
        } else {
          notifyError(data?.message || "Unable to load lease");
        }
      })
      .catch((err) => {
        console.log("lease single err", err);
        notifyError("Something went wrong");
      })
      .finally(() => setDetailLoading(false));
  };

  const doAction = (url, leaseId, label, extra = {}) => {
    setActionBusy(label);
    simplePostCall(url, JSON.stringify({ lease_id: leaseId, ...extra }))
      .then((data) => {
        if (data?.success || data?.result) {
          notifySuccess(data?.message || label);
          openDetail(leaseId);
          getLeases();
        } else {
          notifyError(data?.message || `Unable to ${label}`);
        }
      })
      .catch((err) => {
        console.log("lease action err", err);
        notifyError("Something went wrong");
      })
      .finally(() => setActionBusy(""));
  };

  const commence = (id) =>
    doAction(ApiConfig.FLEET_FINANCE_LEASE_COMMENCE, id, t("Commence"));
  const terminate = (id) =>
    doAction(ApiConfig.FLEET_FINANCE_LEASE_TERMINATE, id, t("Terminate"));

  const openRun = (id) => {
    setRunLeaseId(id);
    setAsOf(todayStr());
    setShowRun(true);
  };

  const runPayments = () => {
    setRunning(true);
    simplePostCall(
      ApiConfig.FLEET_FINANCE_LEASE_RUN,
      JSON.stringify({ lease_id: runLeaseId, as_of: asOf })
    )
      .then((data) => {
        if (data?.success || data?.result) {
          notifySuccess(data?.message || "Lease payments posted");
          setShowRun(false);
          openDetail(runLeaseId);
          getLeases();
        } else {
          notifyError(data?.message || "Unable to post payments");
        }
      })
      .catch((err) => {
        console.log("lease run err", err);
        notifyError("Something went wrong");
      })
      .finally(() => setRunning(false));
  };

  const vehicleLabel = (id) => vehicleNameById(vehicles, id); // U-01

  return (
    <motion.div
      className={sidebar ? "taskMain " : "cx-active taskMain"}
      id="cx-main"
      variants={animations}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.1 }}
    >
      <div id="cx-wrapper" className="Fleet_Finance_Lease">
        <PageHead
          title={t("Leases (IFRS 16)")}
          subtitle={t("ROU asset & lease liability — commence, run & terminate")}
        >
          <button className="cx-btn-2" onClick={() => { setForm(emptyForm); setShowForm(true); }}>
            + {t("Add Lease")}
          </button>
        </PageHead>

        {loading ? (
          <Loader />
        ) : (
          <div className="main-master-wrapper">
            <div id="scroll_insideThe_Padding_tabel">
              <div className="VA_table">
                <table className="table tableAdmin">
                  <thead className="tableHead">
                    <tr>
                      <th>{t("Lease ID")}</th>
                      <th>{t("Vehicle")}</th>
                      <th>{t("Lessor")}</th>
                      <th>{t("Type")}</th>
                      <th>{t("Start")}</th>
                      <th>{t("Term (mo)")}</th>
                      <th>{t("Monthly")}</th>
                      <th>{t("Present Value")}</th>
                      <th>{t("Status")}</th>
                      <th>{t("Action")}</th>
                    </tr>
                  </thead>
                  <tbody className="tableBody">
                    {leases && leases.length > 0 ? (
                      leases.map((l) => (
                        <tr key={l.lease_id}>
                          <td>{l.lease_id}</td>
                          <td>{vehicleLabel(l.vehicle_id)}</td>
                          <td>{l.lessor || "-"}</td>
                          <td>{l.lease_type || "-"}</td>
                          <td>{l.start_date ? toDateDisplay(l.start_date) : "-"}</td>{/* FF-06 */}
                          <td>{l.term_months ?? "-"}</td>
                          <td>{money(l.monthly_payment)}</td>
                          <td>
                            {money(
                              l.rou_asset_initial ??
                                l.lease_liability_initial ??
                                l.present_value
                            )}
                          </td>
                          <td>
                            <StatusBadge value={l.status} />
                          </td>
                          <td>
                            <img
                              src={View}
                              alt="view"
                              style={{ cursor: "pointer" }}
                              onClick={() => openDetail(l.lease_id)}
                            />
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={10}>
                          <NoDataComp />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add lease modal */}
      <Modal show={showForm} onHide={() => setShowForm(false)} centered className="common-model">
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>{t("Add Lease (IFRS 16)")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row">
              <div className="col-md-6 form_input_main">
                <Form.Label className="common-labels">
                  {t("Vehicle")} <span>&#42;</span>
                </Form.Label>
                {/* U-01: searchable vehicle picker */}
                <VehicleSelect
                  name="vehicle_id"
                  vehicles={vehicles}
                  value={form.vehicle_id}
                  onChange={handleChange}
                  placeholder={t("Select Vehicle")}
                />
              </div>
              <div className="col-md-6 form_input_main">
                <Form.Label className="common-labels">{t("Lessor")}</Form.Label>
                <Form.Control type="text" name="lessor" value={form.lessor} onChange={handleChange} placeholder="QIB Ijarah" />
              </div>
              <div className="col-md-6 form_input_main">
                <Form.Label className="common-labels">{t("Lease Type")}</Form.Label>
                <Form.Select name="lease_type" value={form.lease_type} onChange={handleChange}>
                  <option value="finance">finance</option>
                  <option value="operating">operating</option>
                </Form.Select>
              </div>
              <div className="col-md-6 form_input_main">
                <Form.Label className="common-labels">
                  {t("Start Date")} <span>&#42;</span>
                </Form.Label>
                <Form.Control required type="date" name="start_date" value={form.start_date} onChange={handleChange} />
              </div>
              <div className="col-md-4 form_input_main">
                <Form.Label className="common-labels">
                  {t("Term (months)")} <span>&#42;</span>
                </Form.Label>
                <Form.Control required type="number" name="term_months" value={form.term_months} onChange={handleChange} />
              </div>
              <div className="col-md-4 form_input_main">
                <Form.Label className="common-labels">
                  {t("Monthly Payment")} <span>&#42;</span>
                </Form.Label>
                <Form.Control required type="number" name="monthly_payment" value={form.monthly_payment} onChange={handleChange} />
              </div>
              <div className="col-md-4 form_input_main">
                <Form.Label className="common-labels">{t("Discount Rate % (annual)")}</Form.Label>
                <Form.Control type="number" name="discount_rate_annual" value={form.discount_rate_annual} onChange={handleChange} />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer className="pop-up-modal-footer">
            <div className="btn-wrapper">
              <button type="button" className="cx-btn-1" onClick={() => setShowForm(false)}>
                {t("Cancel")}
              </button>
              <button type="submit" className="cx-btn-2" disabled={saving}>
                {saving ? <Spinner animation="border" size="sm" /> : t("Save")}
              </button>
            </div>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Lease detail modal */}
      <Modal show={showDetail} onHide={() => setShowDetail(false)} centered size="lg" className="common-model ff-modal">
        <Modal.Header closeButton>
          <Modal.Title>{t("Lease Detail")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailLoading ? (
            <Loader />
          ) : detail ? (
            <>
              <div className="row" style={{ marginBottom: 12 }}>
                <div className="col-md-4"><b>{t("Lessor")}:</b> {detail.lessor || "-"}</div>
                <div className="col-md-4"><b>{t("Type")}:</b> {detail.lease_type || "-"}</div>
                <div className="col-md-4"><b>{t("Status")}:</b> {detail.status || "-"}</div>
                <div className="col-md-4"><b>{t("Present Value")}:</b> {money(detail.rou_asset_initial ?? detail.lease_liability_initial ?? detail.present_value)}</div>
                <div className="col-md-4"><b>{t("Monthly")}:</b> {money(detail.monthly_payment)}</div>
                <div className="col-md-4"><b>{t("Term")}:</b> {detail.term_months ?? "-"}</div>
              </div>

              <div className="d-flex flex-wrap" style={{ gap: 10, marginBottom: 14 }}>
                <button
                  className="cx-btn-1"
                  onClick={() => commence(detail.lease_id)}
                  disabled={!!actionBusy}
                >
                  {actionBusy === t("Commence") ? <Spinner animation="border" size="sm" /> : t("Commence")}
                </button>
                <button
                  className="cx-btn-2"
                  onClick={() => openRun(detail.lease_id)}
                  disabled={!!actionBusy}
                >
                  {t("Run Payments")}
                </button>
                <button
                  className="cx-btn-1"
                  onClick={() => terminate(detail.lease_id)}
                  disabled={!!actionBusy}
                >
                  {actionBusy === t("Terminate") ? <Spinner animation="border" size="sm" /> : t("Terminate")}
                </button>
              </div>

              <div className="VA_table">
                <table className="table tableAdmin">
                  <thead className="tableHead">
                    <tr>
                      <th>{t("Period")}</th>
                      <th>{t("Payment")}</th>
                      <th>{t("Principal")}</th>
                      <th>{t("Interest")}</th>
                      <th>{t("Liability")}</th>
                      <th>{t("ROU Amort.")}</th>
                      <th>{t("Posted")}</th>
                    </tr>
                  </thead>
                  <tbody className="tableBody">
                    {schedule.length > 0 ? (
                      schedule.map((s, i) => (
                        <tr key={s.ls_id || s.period_no || i}>
                          <td>{s.period_no || s.period || (s.payment_date ? toDateDisplay(s.payment_date) : i + 1)}</td>{/* FF-06 */}
                          <td>{money(s.payment || s.monthly_payment)}</td>
                          <td>{money(s.principal)}</td>
                          <td>{money(s.interest)}</td>
                          <td>{money(s.closing_liability ?? s.liability_balance ?? s.liability)}</td>
                          <td>{money(s.rou_amortization ?? s.rou_amort)}</td>
                          <td>
                            <BoolBadge value={!!(s.is_posted || s.posted)} />
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={7}>
                          <NoDataComp />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <NoDataComp />
          )}
        </Modal.Body>
      </Modal>

      {/* Run payments modal */}
      <Modal show={showRun} onHide={() => setShowRun(false)} centered className="common-model">
        <Modal.Header closeButton>
          <Modal.Title>{t("Run Lease Payments")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ color: "#703c19" }}>
            {t("Posts due monthly payments + ROU amortization up to the date below. Commence the lease first.")}
          </p>
          <div className="form_input_main">
            <Form.Label className="common-labels">{t("As of date")}</Form.Label>
            <Form.Control type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
          </div>
        </Modal.Body>
        <Modal.Footer className="pop-up-modal-footer">
          <div className="btn-wrapper">
            <button type="button" className="cx-btn-1" onClick={() => setShowRun(false)}>
              {t("Cancel")}
            </button>
            <button className="cx-btn-2" onClick={runPayments} disabled={running}>
              {running ? <Spinner animation="border" size="sm" /> : t("Post")}
            </button>
          </div>
        </Modal.Footer>
      </Modal>
    </motion.div>
  );
};

export default Leases;
